import React, { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { GraduationCap, Menu, X, ShieldCheck, MapPin, Phone, Mail, Clock } from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { fetchSite } from "../lib/site";

const SiteContext = createContext({});
export const useSite = () => useContext(SiteContext);

const NAV = [
  { to: "/", label: "Home", key: "home", testId: "site-nav-home", end: true },
  { to: "/courses", label: "Courses", key: "courses", testId: "site-nav-courses" },
  { to: "/verify", label: "Verification", key: "verification", testId: "site-nav-verify" },
  { to: "/about", label: "About Us", key: "about", testId: "site-nav-about" },
  { to: "/contact", label: "Contact Us", key: "contact", testId: "site-nav-contact" },
];

export default function SiteLayout() {
  const [site, setSite] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetchSite().then(setSite).catch(() => {});
  }, []);
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const name = site.institute_name || "Lovely Institute";
  const sections = site.website_sections || {};
  const nav = NAV.filter((n) => sections[n.key] !== false);

  return (
    <SiteContext.Provider value={site}>
      <div className="min-h-screen flex flex-col bg-slate-50">
        {/* header */}
        <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200" data-testid="site-header">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="site-brand">
              {site.logo ? (
                <img src={site.logo} alt={name} className="w-9 h-9 rounded-md object-contain" />
              ) : (
                <div className="w-9 h-9 rounded-md bg-gradient-to-br from-blue-800 to-blue-600 grid place-items-center text-white">
                  <GraduationCap size={20} />
                </div>
              )}
              <span className="font-display font-bold text-slate-900 truncate">{name}</span>
            </Link>
            <nav className="hidden md:flex items-center gap-1 ml-auto">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testId}
                  className={({ isActive }) =>
                    `px-3.5 py-2 rounded-md text-sm font-medium transition-soft ${
                      isActive ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-slate-100"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <button
              className="md:hidden ml-auto p-2 rounded-md text-slate-600 hover:bg-slate-100"
              data-testid="site-menu-btn"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Menu"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          {menuOpen && (
            <nav className="md:hidden border-t border-slate-100 bg-white px-4 py-2" data-testid="site-mobile-menu">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`${n.testId}-m`}
                  className={({ isActive }) =>
                    `block px-3 py-2.5 rounded-md text-sm font-medium ${
                      isActive ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-slate-100"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          )}
        </header>

        <main className="flex-1">
          <Outlet />
        </main>

        {/* footer */}
        <footer className="bg-blue-950 text-blue-100" data-testid="site-footer">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                {site.logo ? (
                  <img src={site.logo} alt="" className="w-9 h-9 rounded-md object-contain bg-white/10" />
                ) : (
                  <div className="w-9 h-9 rounded-md bg-white/10 grid place-items-center text-white">
                    <GraduationCap size={20} />
                  </div>
                )}
                <span className="font-display font-bold text-white">{name}</span>
              </div>
              {site.registration_info && (
                <p className="text-xs text-blue-300">{site.registration_info}</p>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold text-white mb-3">Quick Links</div>
              <ul className="space-y-2 text-sm">
                {nav.map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="hover:text-white transition-soft" data-testid={`footer-link-${n.key}`}>
                      {n.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-sm font-semibold text-white mb-3">Contact</div>
              <ul className="space-y-2 text-sm">
                {site.address && (
                  <li className="flex gap-2"><MapPin size={15} className="shrink-0 mt-0.5" />{site.address}</li>
                )}
                {site.phone && (
                  <li className="flex gap-2"><Phone size={15} className="shrink-0 mt-0.5" />{site.phone}</li>
                )}
                {site.email && (
                  <li className="flex gap-2"><Mail size={15} className="shrink-0 mt-0.5" />{site.email}</li>
                )}
                {site.office_hours && (
                  <li className="flex gap-2"><Clock size={15} className="shrink-0 mt-0.5" />{site.office_hours}</li>
                )}
              </ul>
            </div>
            <div>
              <div className="text-sm font-semibold text-white mb-3">Certificate Verification</div>
              <p className="text-xs text-blue-300 mb-3">
                Verify the authenticity of any certificate or marksheet issued by {name}.
              </p>
              <Link
                to="/verify"
                data-testid="footer-verify-link"
                className="inline-flex items-center gap-2 bg-white text-blue-900 text-sm font-semibold px-4 py-2 rounded-md hover:bg-blue-50 transition-soft"
              >
                <ShieldCheck size={15} /> Verify Certificate
              </Link>
            </div>
          </div>
          <div className="border-t border-white/10 py-4 text-center text-xs text-blue-300">
            © {new Date().getFullYear()} {name}. All rights reserved.
          </div>
        </footer>
        <Toaster position="top-right" richColors />
      </div>
    </SiteContext.Provider>
  );
}
